/**
 * Font catalogue + runtime loader for tenant theming.
 *
 * Two families of fonts are offered in the theme editor:
 *
 *  - BUNDLED_FONTS - the platform's built-in @fontsource families (Cormorant
 *    Garamond, Geist) plus pure system stacks. These ship with the app; no
 *    network load is needed. They are pinned to the top of the picker as the
 *    defaults.
 *  - CURATED_GOOGLE_FONTS - a hand-picked list of common Google Fonts. When a
 *    tenant selects one, `loadGoogleFont` injects a Google Fonts stylesheet
 *    <link> at runtime so the family renders everywhere the CSS variables are
 *    applied (admin SPA, consumer SPA, and - via a server-side @import - the
 *    certificate documents).
 *
 * Fonts are loaded via the v1 CSS API (`fonts.googleapis.com/css`) rather than
 * css2 because v1 silently drops weights a family does not have instead of
 * failing the whole request - so one fixed weight set works for every family.
 */

export type FontCategory =
  | 'sans-serif'
  | 'serif'
  | 'display'
  | 'handwriting'
  | 'monospace'

export interface FontDef {
  /** The Google Fonts family name, e.g. "Open Sans". */
  family: string
  category: FontCategory
}

/** Generic CSS fallback appended to each family, chosen by category. */
const CATEGORY_FALLBACK: Record<FontCategory, string> = {
  'sans-serif': 'sans-serif',
  serif: 'serif',
  display: 'sans-serif',
  handwriting: 'cursive',
  monospace: 'monospace',
}

/**
 * Build the full CSS `font-family` stack stored as the token value, quoting the
 * family name and appending the category's generic fallback.
 */
export function fontStack(def: FontDef): string {
  return `"${def.family}", ${CATEGORY_FALLBACK[def.category]}`
}

/**
 * Platform-bundled + system fonts. These need no network load: Cormorant
 * Garamond and Geist are @fontsource imports in `index.css`; the rest are
 * OS-provided stacks. Pinned to the top of the editor as the defaults.
 */
export interface BundledFontOption {
  label: string
  value: string
}

export const BUNDLED_FONT_OPTIONS: BundledFontOption[] = [
  {
    label: 'Cormorant Garamond (serif)',
    value: '"Cormorant Garamond", Georgia, serif',
  },
  {
    label: 'Geist (sans)',
    value: '"Geist Variable", ui-sans-serif, system-ui, -apple-system, sans-serif',
  },
  {
    label: 'Geist Mono (monospace)',
    value: '"Geist Mono", ui-monospace, SFMono-Regular, monospace',
  },
  {
    label: 'Georgia (system serif)',
    value: 'Georgia, "Times New Roman", serif',
  },
  {
    label: 'System sans',
    value: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
  },
]

/**
 * Curated list of common Google Fonts, grouped visually by category in the
 * editor. Ordered roughly by popularity within each category.
 */
export const CURATED_GOOGLE_FONTS: FontDef[] = [
  // Sans-serif
  { family: 'Inter', category: 'sans-serif' },
  { family: 'Roboto', category: 'sans-serif' },
  { family: 'Open Sans', category: 'sans-serif' },
  { family: 'Lato', category: 'sans-serif' },
  { family: 'Montserrat', category: 'sans-serif' },
  { family: 'Poppins', category: 'sans-serif' },
  { family: 'Nunito', category: 'sans-serif' },
  { family: 'Nunito Sans', category: 'sans-serif' },
  { family: 'Work Sans', category: 'sans-serif' },
  { family: 'Rubik', category: 'sans-serif' },
  { family: 'Mulish', category: 'sans-serif' },
  { family: 'DM Sans', category: 'sans-serif' },
  { family: 'Manrope', category: 'sans-serif' },
  { family: 'Karla', category: 'sans-serif' },
  { family: 'Figtree', category: 'sans-serif' },
  { family: 'Plus Jakarta Sans', category: 'sans-serif' },
  { family: 'Source Sans 3', category: 'sans-serif' },
  { family: 'IBM Plex Sans', category: 'sans-serif' },
  { family: 'Josefin Sans', category: 'sans-serif' },
  { family: 'Quicksand', category: 'sans-serif' },
  { family: 'Barlow', category: 'sans-serif' },
  { family: 'Titillium Web', category: 'sans-serif' },
  { family: 'PT Sans', category: 'sans-serif' },
  { family: 'Hind', category: 'sans-serif' },
  { family: 'Cabin', category: 'sans-serif' },
  // Serif
  { family: 'Playfair Display', category: 'serif' },
  { family: 'Merriweather', category: 'serif' },
  { family: 'Lora', category: 'serif' },
  { family: 'PT Serif', category: 'serif' },
  { family: 'Cormorant Garamond', category: 'serif' },
  { family: 'EB Garamond', category: 'serif' },
  { family: 'Libre Baskerville', category: 'serif' },
  { family: 'Source Serif 4', category: 'serif' },
  { family: 'Bitter', category: 'serif' },
  { family: 'Crimson Text', category: 'serif' },
  { family: 'Noto Serif', category: 'serif' },
  { family: 'Spectral', category: 'serif' },
  { family: 'Frank Ruhl Libre', category: 'serif' },
  // Display
  { family: 'Oswald', category: 'display' },
  { family: 'Raleway', category: 'display' },
  { family: 'Bebas Neue', category: 'display' },
  { family: 'Archivo', category: 'display' },
  { family: 'Abril Fatface', category: 'display' },
  { family: 'Comfortaa', category: 'display' },
  { family: 'Fraunces', category: 'display' },
  // Handwriting
  { family: 'Dancing Script', category: 'handwriting' },
  { family: 'Pacifico', category: 'handwriting' },
  { family: 'Caveat', category: 'handwriting' },
  // Monospace
  { family: 'JetBrains Mono', category: 'monospace' },
  { family: 'Roboto Mono', category: 'monospace' },
  { family: 'Source Code Pro', category: 'monospace' },
  { family: 'IBM Plex Mono', category: 'monospace' },
  { family: 'Space Mono', category: 'monospace' },
]

/** Fast lookup: font-family stack -> the curated Google font it maps to. */
const GOOGLE_STACK_TO_FONT = new Map<string, FontDef>(
  CURATED_GOOGLE_FONTS.map((f) => [fontStack(f), f]),
)

/** Fast lookup: bare family name (lowercased) -> curated Google font. */
const GOOGLE_FAMILY_TO_FONT = new Map<string, FontDef>(
  CURATED_GOOGLE_FONTS.map((f) => [f.family.toLowerCase(), f]),
)

/**
 * Generic / OS families and platform-bundled families that must NOT be fetched
 * from Google - they either resolve locally or are shipped via @fontsource.
 */
const NON_GOOGLE_FAMILIES = new Set(
  [
    'serif',
    'sans-serif',
    'monospace',
    'cursive',
    'fantasy',
    'system-ui',
    'ui-sans-serif',
    'ui-serif',
    'ui-monospace',
    'ui-rounded',
    '-apple-system',
    'blinkmacsystemfont',
    'segoe ui',
    'helvetica',
    'helvetica neue',
    'arial',
    'georgia',
    'times new roman',
    'times',
    'sfmono-regular',
    'sf mono',
    'geist variable',
    'geist mono',
    'geist',
  ].map((s) => s.toLowerCase()),
)

/**
 * Extract the primary family name from a CSS font-family stack - the first
 * entry, with all quotes stripped (a stray mid-name quote would otherwise break
 * the `@import url('...')` interpolation) and whitespace trimmed.
 */
export function primaryFamily(stack: string | undefined | null): string {
  if (!stack) return ''
  const first = stack.split(',')[0]?.trim() ?? ''
  return first.replace(/['"]/g, '').trim()
}

/**
 * Is this stack a Google font we should load over the network? True only when
 * the primary family is a real (non-generic, non-bundled) family name.
 */
export function isGoogleFontStack(stack: string | undefined | null): boolean {
  const family = primaryFamily(stack)
  if (!family) return false
  return !NON_GOOGLE_FAMILIES.has(family.toLowerCase())
}

/**
 * True when the primary family is a platform-bundled (@fontsource) or a
 * generic/OS family - one that resolves locally and is never fetched from
 * Google nor self-hosted. Exposed so callers can reason about font provenance
 * with an explicit, positive check rather than negating `isGoogleFontStack`.
 */
export function isBundledOrSystemFamily(
  stack: string | undefined | null,
): boolean {
  const family = primaryFamily(stack)
  return Boolean(family) && NON_GOOGLE_FAMILIES.has(family.toLowerCase())
}

/** The weights requested for every family (v1 API drops any it lacks). */
const REQUESTED_WEIGHTS = '300,400,500,600,700'

/**
 * Build the Google Fonts v1 stylesheet URL for a family name, or `null` for a
 * generic/system/bundled family that must not be fetched.
 */
export function googleFontHref(stack: string | undefined | null): string | null {
  const family = primaryFamily(stack)
  if (!family || NON_GOOGLE_FAMILIES.has(family.toLowerCase())) return null
  const encoded = family.replace(/ /g, '+')
  return `https://fonts.googleapis.com/css?family=${encoded}:${REQUESTED_WEIGHTS}&display=swap`
}

/** Families already requested this session, so we inject each <link> once. */
const loadedFamilies = new Set<string>()

/**
 * Load a Google font for the given font-family stack by injecting a stylesheet
 * <link> into <head>. Idempotent (deduped by family) and a no-op for
 * generic/system/bundled families or in a non-DOM context.
 */
export function loadGoogleFont(stack: string | undefined | null): void {
  if (typeof document === 'undefined') return
  const family = primaryFamily(stack)
  if (!family) return
  const key = family.toLowerCase()
  if (NON_GOOGLE_FAMILIES.has(key) || loadedFamilies.has(key)) return
  const href = googleFontHref(stack)
  if (!href) return
  loadedFamilies.add(key)
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  link.dataset.googleFont = key
  document.head.appendChild(link)
}

/** Platform baseline stacks - mirror `--font-display` / `--font-sans` in index.css. */
export const DEFAULT_FONT_DISPLAY = '"Cormorant Garamond", Georgia, serif'
export const DEFAULT_FONT_SANS =
  '"Geist Variable", ui-sans-serif, system-ui, -apple-system, sans-serif'

/**
 * Strip any characters a legitimate font-family stack never needs, falling back
 * when the result is empty. Tenant theme values are applied as CSS variables
 * (safe), but reading them back and interpolating into a self-contained HTML
 * `<style>` block (e.g. a certificate document) is an XSS vector - a stored
 * value like `serif; </style><script>...` would otherwise break out. Mirrors
 * `_sanitize_font_stack` in `backend/apps/organisations/branding.py`.
 */
export function sanitizeFontStack(
  stack: string | undefined | null,
  fallback: string,
): string {
  const cleaned = (stack ?? '')
    .replace(/[^A-Za-z0-9 ,"'-]/g, '')
    .trim()
    .replace(/^,+|,+$/g, '')
    .trim()
  return cleaned || fallback
}

/**
 * Build a single Google Fonts `@import` line covering the given font-family
 * stacks, or `''` when none reference a Google-hosted family. For use in
 * self-contained HTML documents (e.g. a certificate `srcDoc`) that cannot rely
 * on runtime `<link>` injection. Mirrors `build_google_fonts_import` in
 * `backend/apps/organisations/branding.py`.
 */
export function googleFontImport(...stacks: (string | undefined | null)[]): string {
  const families: string[] = []
  const seen = new Set<string>()
  for (const stack of stacks) {
    const family = primaryFamily(stack)
    const key = family.toLowerCase()
    if (family && !NON_GOOGLE_FAMILIES.has(key) && !seen.has(key)) {
      seen.add(key)
      families.push(family.replace(/ /g, '+'))
    }
  }
  if (!families.length) return ''
  const spec = families.map((f) => `${f}:${REQUESTED_WEIGHTS}`).join('|')
  return `@import url('https://fonts.googleapis.com/css?family=${spec}&display=swap');`
}

/**
 * Read the tenant's currently-applied display + body font stacks from the
 * document root (set by `applyTenantTheme`), falling back to the platform
 * defaults. Used to theme self-contained HTML documents built in the browser.
 */
export function resolveDocumentFonts(): { display: string; sans: string } {
  if (typeof document === 'undefined') {
    return { display: DEFAULT_FONT_DISPLAY, sans: DEFAULT_FONT_SANS }
  }
  const cs = getComputedStyle(document.documentElement)
  const display = sanitizeFontStack(
    cs.getPropertyValue('--font-display'),
    DEFAULT_FONT_DISPLAY,
  )
  const sans = sanitizeFontStack(cs.getPropertyValue('--font-sans'), DEFAULT_FONT_SANS)
  return { display, sans }
}

export { GOOGLE_STACK_TO_FONT, GOOGLE_FAMILY_TO_FONT }
